/**
 * Backup Utility
 *
 * Utility functions for generating and downloading admin backups.
 * Collects raw data directly from Firestore and generates a downloadable JSON file.
 */
import { database } from '@/plugins/firebase';
import { CURRENT_SEASON_YEAR } from '@/store/seasonYear';
import { BACKUP_COLLECTIONS, BACKUP_VERSION, } from '@/types/BackupTypes';
/**
 * Generates a timestamped filename for the backup file.
 *
 * @returns Filename in format: backup-YYYY-MM-DD-HHmmss.json
 */
export function generateFilename() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `backup-${year}-${month}-${day}-${hours}${minutes}${seconds}.json`;
}
/**
 * Downloads data as a JSON file by creating a Blob and triggering download.
 *
 * @param data - Object to serialize and download
 * @param filename - Name of the downloaded file
 * @throws Error with user-friendly message if memory limit exceeded
 */
export function downloadBackup(data, filename) {
    try {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }
    catch (error) {
        // Re-throw with user-friendly message for memory errors
        if (error instanceof Error && (error.message.includes('memory') || error.message.includes('size') || error.name === 'RangeError')) {
            throw new Error('Záloha je příliš velká pro stažení v prohlížeči');
        }
        throw error;
    }
}
/**
 * Gets the Firestore collection name, with year suffix if applicable.
 *
 * @param config - Collection configuration
 * @returns Firestore collection name
 */
function getFirestoreCollectionName(config) {
    if (config.yearSuffixed) {
        return `${config.name}${CURRENT_SEASON_YEAR}`;
    }
    return config.name;
}
/**
 * Fetches raw data directly from a Firestore collection.
 * Returns documents in the format { id, data } to match existing type structures.
 *
 * @param collectionName - Name of the Firestore collection to fetch
 * @returns Array of documents with id and raw data
 */
async function fetchCollectionData(collectionName) {
    try {
        const snapshot = await database.collection(collectionName).get();
        return snapshot.docs.map((doc) => ({
            id: doc.id,
            data: doc.data(),
        }));
    }
    catch {
        // Return empty array on error - collection will be empty but valid
        return [];
    }
}
/**
 * Generates a complete backup of all Firestore collections and triggers download.
 *
 * Fetches raw data directly from Firestore collections:
 * - activity_log{YEAR}
 * - balances
 * - dashboard
 * - grades
 * - instructors
 * - notifications
 * - pricing
 * - standardOperatingHours
 * - specialOperatingHours{YEAR}
 * - transactions{YEAR}
 * - vouchers
 * - works{YEAR}
 *
 * @returns Result indicating success or failure with relevant details
 */
export async function generateBackup() {
    try {
        const timestamp = new Date().toISOString();
        const filename = generateFilename();
        const backupData = {};
        const collectionNames = [];
        // Fetch data from all configured Firestore collections in parallel
        const fetchPromises = BACKUP_COLLECTIONS.map(async (config) => {
            const collectionName = getFirestoreCollectionName(config);
            const data = await fetchCollectionData(collectionName);
            return { collectionName, data };
        });
        const results = await Promise.all(fetchPromises);
        // Assemble backup data from results
        for (const { collectionName, data } of results) {
            backupData[collectionName] = data;
            collectionNames.push(collectionName);
        }
        // Create metadata
        const metadata = {
            timestamp,
            seasonYear: CURRENT_SEASON_YEAR,
            collections: collectionNames,
            version: BACKUP_VERSION,
        };
        // Assemble final backup object with metadata first
        const finalBackup = {
            _metadata: metadata,
            ...backupData,
        };
        // Trigger download
        downloadBackup(finalBackup, filename);
        return {
            success: true,
            filename,
            timestamp,
        };
    }
    catch (error) {
        let errorMessage = 'Chyba při vytváření zálohy';
        if (error instanceof Error) {
            // Check for memory-related errors (user-friendly message from downloadBackup)
            if (error.message.includes('příliš velká')) {
                errorMessage = error.message;
            }
            else if (error.message.includes('network') || error.message.includes('fetch') || error.message.includes('Network')) {
                errorMessage = 'Chyba při načítání dat';
            }
            else {
                errorMessage = error.message;
            }
        }
        return {
            success: false,
            error: errorMessage,
        };
    }
}
